import React from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const phases = [{
  title: "Prise de contact & visite sur place",
  description: "Nous visitons votre bien et prenons connaissance de votre projet de Travaux : votre besoin, vos envies, vos contraintes... S'ils existent, nous collectons un maximum d'éléments en votre possession (plans, échanges avec la mairie, DPE...)"
}, {
  title: "Esquisses & Conception",
  description: "Sur la base du cahier des charges, nous réalisons les premières esquisses de 2 ou 3 propositions différentes avec une estimation du coût des Travaux par projet. Le\xA0 scénario qui a votre préférence va ensuite pouvoir entrer dans sa phase Conception avec les ajustements sur-mesure qui rendront votre projet unique."
}, {
  title: "Dépôt de la DP ou du PC",
  description: "Nous nous rapprochons de l'entité qui instruira votre dossier (Ville, communauté de communes) et nous constituons tous les éléments nécessaires au dépôt de la DP ou du PC. Si votre maison fait plus de 150m², nous collaborons avec un Architecte DPLG."
}, {
  title: "DCE et Consultation des Entreprises",
  description: "Après l'obtention de la DP ou du PC, nous réalisons le dossier qui permettra de consulter tous les corps de métier. Constitué de plans plus approfondis, du descriptif complet et du quantitatif précis des Travaux, il permet de consulter les entreprises de manière optimisée et comparative."
}, {
  title: "Sélection des entreprises et préparation du chantier",
  description: "Les entreprises ou artisans que nous vous proposons sont des partenaires depuis de nombreuses années, soigneusement sélectionnés pour leur professionnalisme, leur sérieux et leur intelligence relationnelle. Nous analysons ensemble les devis et à l'aide de nos conseils, nous retenons le plus qualifié au prix le plus juste. Une fois retenues, les entreprises lancent les commandes de matériaux et nous ajustons les derniers détails avant le démarrage du chantier."
}, {
  title: "Suivi du chantier et Réception des Travaux",
  description: "Nous organisons l'intervention de tous les corps de métier afin de rythmer minutieusement l'avancement du chantier et garantir le temps nécessaire à une excellente qualité d'exécution. Notre présence quasi quotidienne permet également de gérer les imprévus et contrôler les ouvrages exécutés au plus près du terrain. Indispensable pour une réception des Travaux sereine que nous réaliserons ensemble !"
}];
const TimelineItem = ({
  phase,
  index,
  isLast
}: {
  phase: {
    title: string;
    description: string;
  };
  index: number;
  isLast: boolean;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4
  });
  return <div ref={ref} className="relative pl-12 pb-16 last:pb-0 sm:pl-20" data-index={index}>
      {}
      {!isLast && <div className="absolute left-[19px] top-12 bottom-0 w-0.5 bg-border/20 sm:left-[27px]">
          <div className={`w-full bg-primary transition-all duration-1000 ease-out ${isVisible ? "h-full" : "h-0"}`} />
        </div>}

      {}
      <div className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary bg-background text-primary font-bold shadow-[0_0_20px_hsl(var(--primary)/0.3)] transition-all duration-700 sm:h-14 sm:w-14 sm:text-xl ${isVisible ? "scale-100 opacity-100" : "scale-0 opacity-0"}`}>
        {index + 1}
      </div>

      {}
      <div className={`transition-all duration-700 delay-300 ${isVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"}`}>
        <h3 className="text-xl font-semibold text-foreground mb-3 sm:text-2xl">
          {phase.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed max-w-2xl text-base sm:text-lg">
          {phase.description}
        </p>
      </div>
    </div>;
};
export default function PhasesDeRealisation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return <section id="phases-de-réalisation" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.05)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.05)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">Particuliers, votre projet <span className="text-primary" data-ast-id="f:/src/pages/ProjectManagement/sections/PhasesDeRealisation.tsx|sig:p8yc2l" data-ast-start="4964" data-ast-end="5017">étape par étape</span></h2>
          <p className="text-lg text-muted-foreground">De la première esquisse à la réception des travaux, nous orchestrons chaque phase avec rigueur et expérience pour garantir la réussite de votre projet.</p>
        </div>

        {}
        <div className="max-w-3xl mx-auto">
          {phases.map((phase, index) => <TimelineItem key={index} phase={phase} index={index} isLast={index === phases.length - 1} />)}
        </div>

        {}
        <div className="mt-20 text-center">
          <Button asChild size="lg" className="text-base px-8 h-12 bg-accent hover:bg-accent/90 text-accent-foreground border-none shadow-lg shadow-accent/20">
            <Link to="/ContactPage">
              Démarrer votre projet <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}